/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-03
 */

// Add third-party dependencies.
import { Box, Container, Typography, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

// Add local dependencies.
import toolsDanceLogo from '../../assets/images/tools-dance-logo.svg';
import { getFormattedVersion } from '../../utils/version';

/**
 * Footer component with version display.
 * @example
 * return (
 *   <Footer />
 * )
 * @returns {React.ReactElement} component.
 */
const Footer: React.FC = () => {
  const version = getFormattedVersion();

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: 'background.paper',
        py: 6,
        mt: 8,
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 4,
            alignItems: 'center',
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <img
                src={toolsDanceLogo}
                alt="tools.dance"
                style={{ height: 24, marginRight: 12 }}
              />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                tools.dance
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Smart tools made by dance studio owners for dance studio owners.
            </Typography>
          </Box>
          <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
            <Typography variant="body2" color="text.secondary">
              © 2025 Little Shilling, Inc. • Los Angeles
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              <Link
                component={RouterLink}
                to="/changelog"
                sx={{
                  color: 'inherit',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                    color: 'primary.main',
                  },
                }}
                aria-label="View changelog"
              >
                {version}
              </Link>
              {" • We get you! We got you! You're Good!"}
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
